/**
 * Unified site events – merges CORTEX, TITAN, JELTR, and Archive events for the front page
 * (news, timeline, event dashboard posters). Platform-specific pages and nav still use
 * their own configs (cortexEvents, titanEvents, etc.).
 */

import {
  getCortexEventsForNews,
  getCortexTimelineEntries,
  getCortexEventsForPosters,
} from './cortexEvents';
import {
  getTitanEventsForNews,
  getTitanTimelineEntries,
} from './titanEvents';
import {
  getJeltrEventsForNews,
  getJeltrTimelineEntries,
} from './jeltrEvents';
import {
  getArchiveEventsForNews,
  getArchiveEventsForPosters,
} from './archiveEvents';

/** All news items for the front page, from all platforms, sorted reverse chronologically (newest first) */
export const getEventsForNews = () => {
  const cortex = getCortexEventsForNews().map((e) => ({ ...e, platform: 'cortex' }));
  const titan = getTitanEventsForNews().map((e) => ({ ...e, platform: 'titan' }));
  const jeltr = getJeltrEventsForNews().map((e) => ({ ...e, platform: 'jeltr' }));
  const archive = getArchiveEventsForNews().map((e) => ({ ...e, platform: 'archive' }));
  const all = [...cortex, ...titan, ...jeltr, ...archive];
  return all.sort((a, b) => (b.sortDate || '0000-01-01').localeCompare(a.sortDate || '0000-01-01'));
};

/** All timeline entries for the front page (merged, with sortDate for ordering) */
export const getTimelineEntries = () => {
  const titan = getTitanTimelineEntries();
  const cortex = getCortexTimelineEntries();
  const jeltr = getJeltrTimelineEntries();
  return [...titan, ...cortex, ...jeltr];
};

/** Timeline entries grouped by year (2026, 2025, …), each group sorted reverse chronologically (newest first) */
export const getTimelineEntriesByYear = () => {
  const all = getTimelineEntries().filter((e) => e.sortDate);
  const byYear = {};
  all.forEach((entry) => {
    const year = entry.sortDate.substring(0, 4);
    if (!byYear[year]) byYear[year] = [];
    byYear[year].push(entry);
  });
  Object.keys(byYear).forEach((year) => {
    byYear[year].sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''));
  });
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));
  return years.map((year) => ({ year, entries: byYear[year] }));
};

/** Normalise an event with poster into dashboard poster shape */
const toPosterConfig = (event, platform, id) => {
  const gradient =
    event.accentColor === 'amber'
      ? 'from-amber-500 to-orange-600'
      : event.accentColor === 'orange'
        ? 'from-orange-500 to-yellow-500'
        : event.accentColor === 'yellow'
          ? 'from-yellow-500 to-orange-500'
          : 'from-blue-500 to-purple-600';
  return {
    id,
    platform,
    image: event.posterImage,
    alt: event.posterAlt || event.title,
    link: event.path,
    fallbackGradient: event.fallbackGradient || gradient,
    title: event.title,
    subtitle: event.posterSubtitle || event.dateLabel || '',
  };
};

/** Poster configs for the Event Dashboard: all platforms merged, sorted reverse chronologically (newest first), first 3 */
export const getPostersForDashboard = () => {
  const cortex = getCortexEventsForPosters().map((e) => ({ ...e, platform: 'cortex' }));
  const archive = getArchiveEventsForPosters().map((e) => ({ ...e, platform: 'archive' }));
  const jeltr = getJeltrEventsForNews()
    .filter((e) => e.posterImage)
    .map((e) => ({ ...e, platform: 'jeltr' }));
  const all = [...cortex, ...jeltr, ...archive].sort(
    (a, b) => (b.sortDate || '0000-01-01').localeCompare(a.sortDate || '0000-01-01')
  );
  return all.slice(0, 3).map((e, i) => toPosterConfig(e, e.platform, `${e.platform}-${e.slug}-${i}`));
};
